import type { WorkflowDefinition } from "../types";

/**
 * W3 — Compliance and brand-safety monitoring.
 *
 * `perTarget` on a tag, like W12: a legal or safety topic is not the property,
 * and two topics can be watched at once. The tag is asked for at start
 * (default `compliance`), keys the run, and feeds the `promptTag` predicate
 * through `run.context.tag`, so starting again with the same tag resumes the
 * existing run rather than forking a parallel one.
 *
 * `explicit` because a monitoring programme starts on a date. A property that
 * already had negative messages on it was not already watching this topic.
 */
export const complianceMonitoringWorkflow: WorkflowDefinition = {
	id: "compliance-monitoring",
	wNumber: 3,
	title: "Know when AI says something harmful about the brand",
	outcome:
		"Catch the claims you cannot allow AI to repeat, and find where they came from.",
	audiences: ["pr", "inhouse"],
	uses: [
		"Key Messages",
		"Prompts",
		"Projects",
		"Messages",
		"Citation Intelligence",
	],
	scope: "property",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "tag", label: "Topic tag", defaultTag: "compliance" },
	isApplicable: (mode) => mode.showKeyMessages && mode.showProjects,
	articleSlug: "how-to-monitor-what-ai-says-about-your-brand",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "capture-claims",
			title: "Write down the claims you cannot allow",
			page: "Key Messages",
			route: "/key-messages",
			criterion: "You've added a harmful claim since you started.",
			why: "Legal and comms need a written list of what must never be said. A claim nobody wrote down cannot be tracked.",
			docsHref: "/docs/dashboards/key-messages",
			tasks: [
				"Add each off-limits claim as a negative key message",
				"Keep each one to a single idea",
				"Tag them with the topic you are watching",
			],
			// The list is written for *this* topic. Whatever negative messages
			// the property already carried are somebody else's programme.
			complete: { kind: "since", event: "negativeKeyMessageAdded" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Harmful claims tracked",
							value: state.property.keyMessages.negative,
							target: 1,
						}
					: null,
		},
		{
			id: "topic-prompts",
			title: "Add the questions that would surface the claim",
			page: "Prompts",
			route: "/discovery-queries",
			criterion: "You've tagged prompts for this topic.",
			why: "A harmful claim only appears when someone asks the question that draws it out. The tag lets every dashboard filter to this topic.",
			docsHref: "/docs/dashboards/discovery-queries",
			tasks: [
				"Write the safety, ethics and reliability questions a buyer would ask",
				"Add the questions a journalist or a regulator would ask",
				"Tag them all with the topic",
			],
			complete: { kind: "promptTag", tag: "compliance" },
			signal: ({ state, run }) => {
				if (!state.property) {
					return null;
				}
				const tag = run?.context.tag ?? "compliance";
				return {
					kind: "checklist",
					items: [
						{
							label: `Prompts tagged "${tag}"`,
							done: state.property.promptTags.includes(tag),
						},
					],
				};
			},
		},
		{
			id: "daily-project",
			title: "Put those prompts on a daily schedule",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've created a project since you started.",
			why: "A project asks the tagged questions every day. Waiting for someone to check by hand is how a harmful answer goes unnoticed.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Create a General project named for the topic",
				"Add the tagged prompts to it",
			],
			// The topic gets its own daily schedule; a project the team was
			// already keeping is not a daily read on these questions.
			complete: { kind: "since", event: "projectCreated" },
			spend: { kind: "trackedPrompts", count: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Active projects",
							value: state.property.activeProjectCount,
							target: 1,
						}
					: null,
		},
		{
			id: "alerts",
			title: "Check who gets negative message alerts",
			page: "Settings",
			route: "/settings?tab=notifications#negative-message-alerts",
			criterion: "At least one person gets these alerts.",
			why: "Legal and comms should hear about a harmful answer in their inbox. A dashboard nobody opens is not monitoring.",
			docsHref: "/docs/dashboards/settings",
			tasks: [
				"Check that the legal and comms leads are on the negative message alerts list",
				"Add outside counsel or agency addresses if they should get them too",
			],
			requires: "negativeMessageAlerts",
			complete: {
				kind: "count",
				key: "negativeMessageAlertRecipientCount",
				gte: 1,
			},
		},
		{
			id: "watch-by-tag",
			title: "Watch the claims topic by topic",
			page: "Messages",
			route: "/dashboard/messages",
			criterion: "You've got daily data for the tagged prompts.",
			why: "This is where you see how often a harmful claim reaches an answer. One sighting matters more here than a trend line.",
			docsHref: "/docs/dashboards/key-messages",
			tasks: [
				"Filter the messages drill-down to the topic tag",
				"Check the harmful claims against your notes each day",
			],
			complete: { kind: "flag", key: "hasNightlyMetrics" },
			enrichment: "messageTrend",
			// The real series comes from the message-tracking hook in the step
			// panel; the loader deliberately carries no time series.
			signal: () => null,
		},
		{
			id: "trace-sources",
			title: "Find the sources feeding the claim",
			page: "Messages",
			route: "/dashboard/messages",
			criterion: "You've identified the sources behind the claim.",
			why: "AI cites the pages it read. Tracing them tells you whether the claim comes from one outlet or from your own site.",
			docsHref: "/docs/dashboards/key-messages",
			tasks: [
				"Open the matched answers for the harmful claim",
				"Read the Sources tab for the outlets cited in those answers",
				"Take that list to whoever can get it corrected",
			],
			requires: "messageMatches",
			// Manual on purpose: nothing watches "you read the source list".
			complete: { kind: "manual" },
		},
	],
};
